import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FOOTER_COLUMNS } from "@/lib/nav";
import Logo, { LogoMark } from "@/components/Logo";
import { T } from "@/constants/testIds";

export default function Footer() {
  return (
    <footer data-testid={T.footer} className="relative bg-ink text-background/85 overflow-hidden">
      {/* Giant faint brand mark, bottom right */}
      <LogoMark className="absolute -bottom-24 -right-16 h-[440px] w-auto opacity-[0.05] pointer-events-none text-background rotate-[-6deg]" />

      {/* Call-to-action band */}
      <div className="container-editorial pt-20 pb-14 border-b border-background/10 relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.24em] text-gold">अनन्त जीवन · Anant Jeevan</span>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.08] text-background mt-4" style={{ fontWeight: 500 }}>
              Discover <em className="italic text-gold">eternal life</em> today.
            </h2>
            <p className="text-background/60 mt-4 max-w-lg leading-relaxed text-[15px]">
              “This is eternal life, that they know You, the only true God, and Jesus Christ whom You have sent.”
              <span className="text-background/40"> — John 17:3</span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 shrink-0">
            <Link data-testid={T.footerLink("cta-start-here")} to="/start-here" className="btn-gold">
              Start Here <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              data-testid={T.footerLink("cta-prayer")}
              to="/contact#prayer"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium tracking-wide uppercase border border-background/25 text-background transition-[border-color,background-color] duration-300 hover:border-gold hover:bg-background/5"
              style={{ letterSpacing: "0.08em", borderRadius: "2px" }}
            >
              Request Prayer
            </Link>
          </div>
        </div>
      </div>

      {/* Link columns */}
      <div className="container-editorial py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-12">
          <div className="md:col-span-4">
            <Link to="/" aria-label="Anant Jeevan — Home" className="inline-block transition-opacity hover:opacity-85">
              <Logo dark />
            </Link>
            <p className="text-sm text-background/65 mt-6 max-w-sm leading-relaxed">
              An independent Christian teaching platform helping people explore the Bible, understand the Gospel,
              know Jesus Christ, and discover the eternal life God offers through Him.
            </p>
            <p className="font-serif italic text-sm text-gold/90 mt-5 max-w-sm leading-relaxed">
              “I am the vine; you are the branches… he it is that bears much fruit.”
              <span className="not-italic font-sans text-[11px] tracking-widest uppercase text-background/45 ml-2">John 15:5</span>
            </p>
            <p className="text-xs text-background/40 mt-6 leading-relaxed max-w-sm">
              Scripture quotations marked NIV, ESV, or NKJV are shown as short permitted excerpts with attribution.
              KJV is public domain.
            </p>
          </div>
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <h4 className="text-[11px] uppercase tracking-[0.24em] text-gold mb-5 flex items-center gap-2">
                <span className="h-px w-4 bg-gold/60" />
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.slug}>
                    <Link
                      data-testid={T.footerLink(l.slug)}
                      to={l.to}
                      className="group inline-flex items-center gap-1.5 text-sm text-background/70 transition-colors duration-200 hover:text-gold"
                    >
                      <span>{l.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-background/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-background/50">
          <p>© {new Date().getFullYear()} Anant Jeevan · अनन्त जीवन. All content editorial and Scripture-referenced.</p>
          <div className="flex items-center gap-5">
            <Link data-testid={T.footerLink("grievance")} to="/legal/grievance" className="transition-colors hover:text-gold">
              Grievance Contact
            </Link>
            <Link data-testid={T.footerLink("corrections")} to="/legal/corrections" className="transition-colors hover:text-gold">
              Editorial Corrections
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
